import React, { memo, useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';

import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import RecordIcon from '@smart-tv/components/icons/record';
import { CancelRecordIcon } from '@smart-tv/components/icons/cancel-recording';
import ProgressSpinner from '@smart-tv/components/progress-spinner/progress-spinner';
import { PLAYER_FOCUS_KEY_RECORD } from '@smart-tv/components/player/player-helpers';
import { LiveTvChannel } from '@smart-tv/typings/content';
import { IconTypes, useToast } from '@smart-tv/hooks/use-toast/use-toast';
import startRecording from '@smart-tv/utils/kmm/startRecording';
import deleteRecording from '@smart-tv/utils/kmm/deleteRecording';
import testIds from '@smart-tv/e2e/shared/variables';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';
import { rootStore as npvrRootStore } from '@smart-tv/components/npvr/_stores/store-root';
import { NpvrResourceKey } from '@smart-tv/components/npvr/_stores/store-npvr';
import {
  BaseError,
  networkError,
  networkErrorMessage
} from '@smart-tv/contexts/error-context/error-context';

import usePlayerRecording from '../../_hooks/use-player-recording';
import useDeactivatePlayerActions from '../_hooks/use-deactivate-player-actions';
import { autoHideTimeoutExtensionAtom } from '../_hooks/use-player-controls';
import { playerBtnsFocusHistoryAtom } from './use-player-controls-navigation';

const AUTO_HIDE_EXTENSION_MS = 3000;

interface Props {
  channel?: LiveTvChannel;
}

const RecordButton = ({ channel }: Props) => {
  const { showToast } = useToast();
  const isDeactivated = useDeactivatePlayerActions();
  const setPlayerBtnsFocusHistory = useSetAtom(playerBtnsFocusHistoryAtom);
  const setAutoHideTimeoutExtension = useSetAtom(autoHideTimeoutExtensionAtom);

  const { canRecord, isRecording, setIsRecording, sid, eventId } = usePlayerRecording(channel);
  const [isRecordingBeingSet, setIsRecordingBeingSet] = useState(false);
  const { refresh: refreshNpvrNotifications } = useNpvrNotifications();

  const handleToggleRecord = useCallback(async () => {
    if (sid === undefined || eventId === undefined) return;

    setIsRecordingBeingSet(true);
    setAutoHideTimeoutExtension(AUTO_HIDE_EXTENSION_MS);

    try {
      if (isRecording) {
        if (await deleteRecording(sid, eventId)) {
          setIsRecording(false);
          // A single cancel here can mean either a scheduled-only or an already-recorded
          // event - removeRecording covers recordings + expiringSoon, scheduled is still
          // invalidated since we don't have a full NpvrEventItem to match it against.
          npvrRootStore.npvrStore.removeRecording(eventId);
          npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
          showToast('Optagelsen af hele dette program blev annulleret', IconTypes.success);
        } else {
          showToast('Der opstod en fejl under annulleringen af optagelsen', IconTypes.failure);
        }
      } else {
        if (await startRecording(sid, eventId)) {
          setIsRecording(true);
          npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
          showToast('Optagelse planlagt', IconTypes.success);
        }
      }
    } catch (error) {
      if (error === networkError || (error as BaseError).offline) {
        showToast(networkErrorMessage, IconTypes.failure);
      }
      console.error(error);
    } finally {
      setIsRecordingBeingSet(false);
      setAutoHideTimeoutExtension(0);
      refreshNpvrNotifications();
    }
  }, [sid, eventId, isRecording, showToast, setIsRecording, setAutoHideTimeoutExtension, refreshNpvrNotifications]);

  if (!canRecord) return null;

  return (
    <IconButton
      size="xl"
      focusKey={PLAYER_FOCUS_KEY_RECORD}
      onClick={() => !isDeactivated && !isRecordingBeingSet && handleToggleRecord()}
      onNavFocus={() => setPlayerBtnsFocusHistory(PLAYER_FOCUS_KEY_RECORD)}
      data-testid={
        isRecording
          ? testIds.player.playerControls.cancelRecording
          : testIds.player.playerControls.startRecording
      }
      icon={
        isRecordingBeingSet ? (
          <ProgressSpinner icon={true} />
        ) : isRecording ? (
          <CancelRecordIcon />
        ) : (
          <RecordIcon />
        )
      }
    >
      {isRecording ? 'Annuller optagelse' : 'Optag'}
    </IconButton>
  );
};

export default memo(RecordButton);
